import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import {
  ProductCardDataLayerContainer,
} from '../../containers/ProductCardDataLayerContainer/ProductCardDataLayerContainer';
import {
  TinyProductCardDataLayerContainer,
} from '../../containers/TinyProductCardDataLayerContainer/TinyProductCardDataLayerContainer';
import { ITEM_POSITION_SHIFT } from '../../hooks/productListHooks/productListHooks';
import { LayoutExtension } from '../../types/ResponsiveState';
import { ProductType } from '../../types/apiEnums';
import { ColorPresentational, ImagePresentational, ProductCardType } from '../../types/common';
import { ItemListID } from '../../types/dataLayer';
import { Typography } from '../Typography/Typography';
import styles from './SearchResults.module.css';

const messages = defineMessages({
  results: {
    id: 'src.components.SearchResults.results',
    defaultMessage: '{count, plural, one {1 result} other {{count} results}}',
  },
  noResults: {
    id: 'src.components.SearchResults.noResults',
    defaultMessage: 'Oh no! No results found. Check the spelling or try a different search term.',
  },
});

export type SearchResult = {
  backgroundColor: ColorPresentational;
  heroTitle?: string;
  id: string;
  image: ImagePresentational;
  path: string | null;
  productType: ProductType;
  showShadow: boolean;
  title: string;
};

export type Props = {
  layoutExtension: LayoutExtension;
  results?: SearchResult[] | null;
};

export const SearchResults = React.memo(({ results, layoutExtension }: Props) => {
  return (
    <div className={styles.searchResults}>
      <Typography component='div' variant='body2' className={styles.title}>
        <FormattedMessage {...messages.results} values={{
          count: results ? results.length : 0,
        }} />
      </Typography>

      {(!results || results.length === 0)
        && <Typography className={styles.noResults}>
          <FormattedMessage {...messages.noResults} />
        </Typography>}

      <div className={styles.resultsContainer}>
        {
          results ? results.map((product, idx) => (
            layoutExtension === LayoutExtension.Mobile ? (
              <TinyProductCardDataLayerContainer
                key={product.id}
                id={product.id}
                position={idx + ITEM_POSITION_SHIFT}
                title={product.title ?? product.heroTitle}
                cardImage={product.image}
                path={product.path}
                productType={product.productType}
                itemListID={ItemListID.SEARCH_RESULTS}
              />
            ) : (
              <div key={product.id} className={styles.productCardContainer}>
                <ProductCardDataLayerContainer
                  id={product.id}
                  position={idx + ITEM_POSITION_SHIFT}
                  title={product.title ?? product.heroTitle}
                  cardImage={product.image}
                  backgroundColor={product.backgroundColor}
                  path={product.path}
                  showHand={false}
                  cardType={ProductCardType.S}
                  productType={product.productType}
                  itemListID={ItemListID.SEARCH_RESULTS}
                  showShadow={product.showShadow}
                />
              </div>
            )
          )) : null
        }
      </div>
    </div>
  );
});

SearchResults.displayName = 'SearchResults';
